import { useQuery } from '@apollo/client'
import { Add, Close, MoreVert, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { groupBy } from 'lodash'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'

import CenteredBox from '@/components/CenteredBox'
import { DaiDivider } from '@/components/DaiDivider'
import StickyHeader from '@/components/StickyHeader'
import { StoreHeader } from '@/components/stores/StoreHeader'
import { TaskChip } from '@/components/tasks/TaskChip'
import { useRefresh, useRefreshContext } from '@/contexts/RefreshContext'
import {
  ActionRepeatSchedule,
  ActionType,
  GET_ACTION_GENERATORS,
  GET_TENANT_STORES_SIMPLE,
  GetActionGeneratorsQuery,
  GetActionGeneratorsQueryVariables,
  GetTenantStoresSimpleQuery,
  GetTenantStoresSimpleQueryVariables,
  Ordering,
} from '@/graphql'
import { useTablePageState } from '@/hooks/useTablePageState'
import useT from '@/i18n/useT'
import { DateHelpers } from '@/utils/date.helpers'

import DeliciousTable, { HeadCell } from '../components/DeliciousTable'
import { CreateRestockAction } from '../forms/task/CreateRestockAction'
import { DeleteActionGenerator } from '../forms/task/DeleteActionGenerator'
import {
  EditRestockTimes,
  EditRestockTimesActionGenerator,
} from '../forms/task/EditRestockTimes'

dayjs.extend(advancedFormat)

type RestockTimesCardProps = {
  storeId: string
  onEditClick: (
    actionGenerators: GetActionGeneratorsQuery['tenantActionGenerators']['results'],
  ) => void
  onDeleteClick: (actionGenIds: string[]) => void
}
type RestockTimeGroupProps = {
  title: string
  index: number
  actionGen: GetActionGeneratorsQuery['tenantActionGenerators']['results']
  onDeleteClick: (actionGenIds: string[]) => void
  onEditClick: (
    actionGenerators: GetActionGeneratorsQuery['tenantActionGenerators']['results'],
  ) => void
}
const RestockTimeGroup: React.FC<RestockTimeGroupProps> = props => {
  const today = dayjs().format('YYYY-MM-DD')
  const { t } = useT()
  const { title, index, actionGen, onEditClick, onDeleteClick } = props
  const [anchorMenu, setAnchorMenu] = useState<any>(null)

  const toggleMenu = (event: React.SyntheticEvent) => {
    setAnchorMenu(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorMenu(null)
  }
  return (
    <Box key={title}>
      {index > 0 && <DaiDivider my={undefined} mb={4} mt={2} />}
      <Stack
        key={title}
        spacing={2}
        direction="row"
        justifyContent={'space-between'}
      >
        <Typography variant="subtitle1">
          {(() => {
            const [titleName, actionTypeRaw] = title.split('||')
            if (!actionTypeRaw) return titleName
            const actionTypeLabel =
              actionTypeRaw === ActionType.PRODUCT_CAPTURE
                ? 'Planogram Compliance'
                : actionTypeRaw === ActionType.PICTURE
                  ? 'Single Photo'
                  : 'No Photo'
            return `${titleName} - ${actionTypeLabel}`
          })()}
        </Typography>
        <Stack flexDirection={'row'} spacing={2} alignItems={'center'}>
          <Stack spacing={1} direction="column">
            {[...actionGen]
              .sort((a, b) =>
                DateHelpers.sortTime(a.dueDateTime, b.dueDateTime),
              )
              .map(action => (
                <Stack
                  direction="row"
                  spacing={1}
                  key={action.dueDateTime}
                  alignItems="center"
                >
                  <TaskChip
                    label={dayjs(`${today}${action.dueDateTime}`).format(
                      'h:mm a',
                    )}
                    status={'green'}
                    sx={{ width: 100 }}
                  />
                  {action.repeatSchedule && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 140, textAlign: 'left' }}
                    >
                      {action.repeatSchedule === ActionRepeatSchedule.WEEKLY
                        ? `Weekly on ${dayjs(action.startDate?.substring(0, 10)).format('dddd')}`
                        : action.repeatSchedule === ActionRepeatSchedule.MONTHLY
                          ? `Monthly on the ${dayjs(action.startDate?.substring(0, 10)).format('Do')}`
                          : action.repeatSchedule}
                    </Typography>
                  )}
                </Stack>
              ))}
          </Stack>
          <IconButton color="secondary" onClick={toggleMenu}>
            <MoreVert />
          </IconButton>
        </Stack>
        <Menu
          id="menu-restock"
          anchorEl={anchorMenu}
          open={Boolean(anchorMenu)}
          onClose={closeMenu}
        >
          {(() => {
            const repeatScheduleRaw =
              actionGen[0]?.repeatSchedule || ActionRepeatSchedule.DAILY
            if (repeatScheduleRaw === ActionRepeatSchedule.DAILY) {
              return (
                <MenuItem onClick={() => onEditClick(actionGen)}>
                  {t('common.editTimes')}
                </MenuItem>
              )
            } else {
              const groupedByStartDate = groupBy(actionGen, a =>
                a.startDate ? a.startDate.substring(0, 10) : '',
              )
              return Object.entries(groupedByStartDate).map(
                ([dateStr, gens]) => {
                  const dayLabel =
                    repeatScheduleRaw === ActionRepeatSchedule.WEEKLY
                      ? dayjs(dateStr).format('dddd')
                      : dayjs(dateStr).format('Do')
                  return (
                    <MenuItem key={dateStr} onClick={() => onEditClick(gens)}>
                      {t('common.edit')} {dayLabel} {t('common.restockTimes')}
                    </MenuItem>
                  )
                },
              )
            }
          })()}
          <Divider />
          <MenuItem onClick={() => onDeleteClick(actionGen.map(a => a.id))}>
            {t('common.delete')}
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  )
}
const RestockTimesCard: React.FC<RestockTimesCardProps> = props => {
  const { storeId, onEditClick, onDeleteClick } = props

  const {
    data: actions,
    loading: loadingActions,
    refetch: refetchActions,
  } = useQuery<GetActionGeneratorsQuery, GetActionGeneratorsQueryVariables>(
    GET_ACTION_GENERATORS,
    {
      variables: {
        filters: {
          endDate: { isNull: true },
          isRestock: true,
          stores: {
            id: storeId,
          },
        },
      },
      notifyOnNetworkStatusChange: true,
    },
  )

  useRefresh(refetchActions)

  const actionsGrouped =
    groupBy(
      actions?.tenantActionGenerators.results,
      a => `${a.title}||${a.actionType}||${a.repeatSchedule}`,
    ) || {}

  return loadingActions ? (
    <Stack>
      <Skeleton />
      <Skeleton />
    </Stack>
  ) : (
    <Stack spacing={2} direction={'column'}>
      {Object.entries(actionsGrouped).map(([key, actionGen], index) => (
        <RestockTimeGroup
          key={key}
          title={key}
          actionGen={actionGen}
          index={index}
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
        />
      ))}
      {actions?.tenantActionGenerators.results.length === 0 && (
        <EmptyRestocks />
      )}
    </Stack>
  )
}

const EmptyRestocks = () => {
  return (
    <CenteredBox>
      <Typography>
        No restocks found for this store, add some to start collecting data!
      </Typography>
    </CenteredBox>
  )
}

const RestockSetupPage: React.FC = () => {
  const today = dayjs().format('YYYY-MM-DD')
  const { t } = useT()
  const [openDialog, setOpenDialog] = useState(false)
  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 5,
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 800)
  const [actionGeneratorsToUpdate, setActionGeneratorsToUpdate] = useState<
    EditRestockTimesActionGenerator[]
  >([])
  const [actionGeneratorsToUpdateStoreId, setActionGeneratorsToUpdateStoreId] =
    useState<string | null>(null)
  const [generatorIdsToDelete, setGeneratorIdsToDelete] = useState<
    string[] | null
  >(null)
  const { refresh } = useRefreshContext()
  const { id: singleStoreId } = useParams()

  const {
    data: storeData,
    loading: loadingStores,
    refetch: refetchStores,
    error: storeError,
  } = useQuery<GetTenantStoresSimpleQuery, GetTenantStoresSimpleQueryVariables>(
    GET_TENANT_STORES_SIMPLE,
    {
      variables: {
        filters: {
          onlyMyStores: true,
          AND: {
            id: singleStoreId || undefined,
            AND: {
              isActive: true,
              AND: {
                location_Name: { iContains: debouncedSearchTerm },
                OR: {
                  location_State: { iContains: debouncedSearchTerm },
                  OR: {
                    location_City: { iContains: debouncedSearchTerm },
                    OR: {
                      location_Address1: { iContains: debouncedSearchTerm },
                    },
                  },
                },
              },
            },
          },
        },
        order: {
          name: Ordering.ASC,
        },
        pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
      },
    },
  )

  useRefresh(refetchStores)

  const headCells: HeadCell<object>[] = [
    {
      id: 'name',
      alignment: 'left',
      label: t('storeList.columns.name'),
    },
    {
      id: 'actions',
      alignment: 'right',
      label: t('restockSetup.actionTimes'),
    },
  ]

  const renderRow = (
    store: GetTenantStoresSimpleQuery['tenantStores']['results'][0],
    index: number,
  ) => (
    <TableRow tabIndex={-1} key={`${store.id}-${index}`}>
      <TableCell>
        <StoreHeader
          name={store.location?.name || ''}
          address1={store.location?.address1 || ''}
          city={store.location?.city || ''}
          state={store.location?.state || ''}
          postalCode={store.location?.postalCode || ''}
        />
      </TableCell>
      <TableCell>
        <RestockTimesCard
          storeId={store.id}
          onEditClick={generators => {
            setActionGeneratorsToUpdateStoreId(store.id)
            setActionGeneratorsToUpdate(
              generators.map(g => ({
                ...g,
                dueDateTime: `${today}${g.dueDateTime}`,
              })),
            )
          }}
          onDeleteClick={actionGenId => {
            setGeneratorIdsToDelete(actionGenId)
          }}
        />
      </TableCell>
    </TableRow>
  )

  return (
    <>
      <Helmet title="Restocks" />
      <StickyHeader>
        <Stack
          direction={'row'}
          spacing={3}
          alignItems={'center'}
          mt={3}
          mb={3}
          justifyContent={'flex-end'}
        >
          {!singleStoreId && (
            <TextField
              id="restockStoresSearch"
              type="text"
              name="restockStoresSearch"
              label={t('stores.searchStore')}
              value={searchTerm}
              fullWidth
              placeholder={t('stores.searchStoreHelperText')}
              onChange={e => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchTerm('')}
                        size="small"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          <Button
            sx={{ width: 200, height: 50 }}
            startIcon={<Add />}
            variant="contained"
            onClick={() => setOpenDialog(true)}
          >
            {t('restockSetup.addRestock')}
          </Button>
        </Stack>
        <DaiDivider />
      </StickyHeader>
      <DeliciousTable<
        GetTenantStoresSimpleQuery['tenantStores']['results'][0],
        object
      >
        data={storeData?.tenantStores.results || []}
        headCells={headCells}
        renderRow={renderRow}
        loading={loadingStores}
        error={storeError?.message}
        totalCount={storeData?.tenantStores?.totalCount || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
      <CreateRestockAction
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        storeId={singleStoreId}
      />
      <DeleteActionGenerator
        open={!!generatorIdsToDelete}
        onClose={() => setGeneratorIdsToDelete(null)}
        onSuccess={() => {
          setGeneratorIdsToDelete(null)
          refresh()
        }}
        ids={generatorIdsToDelete}
      />
      <EditRestockTimes
        onClose={() => {
          setActionGeneratorsToUpdate([])
          setActionGeneratorsToUpdateStoreId(null)
        }}
        onSuccess={() => {
          setActionGeneratorsToUpdate([])
          setActionGeneratorsToUpdateStoreId(null)
          refresh()
        }}
        open={actionGeneratorsToUpdate.length > 0}
        initialGenerators={actionGeneratorsToUpdate}
        storeId={actionGeneratorsToUpdateStoreId}
      />
    </>
  )
}

export default RestockSetupPage
